import { z } from 'zod';
import {
  CLIENT_STATUSES,
  CLIENT_TYPES,
  FOLLOWUP_CHANNELS,
  LEAD_ACTIVITY_TYPES,
  LEAD_STATUSES,
  LEAD_TEMPERATURES,
  PRIORITIES,
} from '../constants';
import {
  addressSchema,
  objectIdSchema,
  optionalDate,
  optionalEmail,
  optionalObjectId,
  optionalPhone,
  optionalText,
  paginationQuerySchema,
  patchOf,
  tagsSchema,
} from './common';

const stageKey = z.string().trim().min(1).max(40);

export const leadCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  company: optionalText(120),
  email: optionalEmail(),
  phone: optionalPhone(),
  source: z.string().trim().max(40).default('other'),
  stage: stageKey.optional(),
  temperature: z.enum(LEAD_TEMPERATURES).default('warm'),
  priority: z.enum(PRIORITIES).default('medium'),
  estimatedValue: z.coerce.number().min(0).max(1e12).nullable().optional(),
  requirement: optionalText(2000),
  ownerId: optionalObjectId(),
  nextFollowUpAt: optionalDate(),
  tags: tagsSchema,
  /** Create even if a possible duplicate exists. */
  allowDuplicate: z.boolean().default(false),
});
export type LeadCreateInput = z.input<typeof leadCreateSchema>;

export const leadUpdateSchema = patchOf(leadCreateSchema.omit({ ownerId: true, allowDuplicate: true, stage: true }));
export type LeadUpdateInput = z.input<typeof leadUpdateSchema>;

export const leadAssignSchema = z.object({
  ownerId: objectIdSchema,
  sharedWith: z.array(objectIdSchema).max(20).optional(),
  note: optionalText(500),
});

export const leadStageSchema = z.object({
  stage: stageKey,
  lostReason: optionalText(500),
});

export const leadActivitySchema = z.object({
  type: z.enum(LEAD_ACTIVITY_TYPES),
  summary: z.string().trim().min(1).max(2000),
  outcome: optionalText(200),
  occurredAt: optionalDate(),
});
export type LeadActivityInput = z.input<typeof leadActivitySchema>;

export const followupCreateSchema = z.object({
  dueAt: z.coerce.date(),
  channel: z.enum(FOLLOWUP_CHANNELS).default('call'),
  notes: optionalText(1000),
  assigneeId: optionalObjectId(),
});
export type FollowupCreateInput = z.input<typeof followupCreateSchema>;

export const followupCompleteSchema = z.object({
  outcome: z.string().trim().min(1).max(200),
  notes: optionalText(1000),
  next: followupCreateSchema.omit({ assigneeId: true }).nullable().optional(),
});
export type FollowupCompleteInput = z.input<typeof followupCompleteSchema>;

export const leadConvertSchema = z.object({
  clientName: optionalText(120),
  /** Link to an existing client instead of creating a new one. */
  existingClientId: optionalObjectId(),
});

export const leadListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(LEAD_STATUSES).optional(),
  stage: z.string().max(40).optional(),
  temperature: z.enum(LEAD_TEMPERATURES).optional(),
  source: z.string().max(40).optional(),
  ownerId: z.union([objectIdSchema, z.literal('unassigned'), z.literal('me')]).optional(),
  followup: z.enum(['today', 'overdue', 'upcoming']).optional(),
});
export type LeadListQuery = z.input<typeof leadListQuerySchema>;

export const followupListQuerySchema = paginationQuerySchema.extend({
  due: z.enum(['today', 'overdue', 'upcoming', 'all']).default('all'),
  status: z.enum(['pending', 'completed', 'missed', 'cancelled']).optional(),
  assigneeId: z.union([objectIdSchema, z.literal('me')]).optional(),
});

export const clientContactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  designation: optionalText(100),
  email: optionalEmail(),
  phone: optionalPhone(),
  isPrimary: z.boolean().default(false),
});

export const clientCreateSchema = z.object({
  name: z.string().trim().min(1).max(160),
  type: z.enum(CLIENT_TYPES).default('company'),
  email: optionalEmail(),
  phone: optionalPhone(),
  website: optionalText(200),
  gstin: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
    z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[0-9A-Z]{15}$/, 'GSTIN must be 15 characters')
      .nullable()
      .optional(),
  ),
  industry: optionalText(100),
  category: optionalText(60),
  status: z.enum(CLIENT_STATUSES).default('active'),
  billingAddress: addressSchema.optional(),
  officeAddress: addressSchema.optional(),
  relationshipManagerId: optionalObjectId(),
  contacts: z.array(clientContactSchema).max(20).default([]),
  tags: tagsSchema,
  notes: optionalText(4000),
});
export type ClientCreateInput = z.input<typeof clientCreateSchema>;

export const clientUpdateSchema = patchOf(clientCreateSchema);
export type ClientUpdateInput = z.input<typeof clientUpdateSchema>;

export const clientListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(CLIENT_STATUSES).optional(),
  relationshipManagerId: z.union([objectIdSchema, z.literal('me')]).optional(),
  category: z.string().max(60).optional(),
});

export const clientNoteSchema = z.object({
  summary: z.string().trim().min(1).max(2000),
  type: z.enum(LEAD_ACTIVITY_TYPES).default('note'),
});
